import { isTextOverlay } from "../lib/utils";
import { AnyOverlay, TextOverlay } from "../models/Post";

/**
 * Generates a unique ID for text overlays
 */
export function generateUniqueId(): string {
  return `text-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Returns a zIndex value that will place a new overlay on top of all existing ones.
 */
export function getNextZIndex(existingOverlays: AnyOverlay[]): number {
  if (existingOverlays.length === 0) return 1;
  const maxZ = Math.max(...existingOverlays.map((o) => o.zIndex ?? 0));
  return maxZ + 1;
}

/**
 * Reassigns zIndex values to match array order (last element = top layer).
 * Used after drag-and-drop reorder to keep zIndex in sync with visual order.
 */
export function syncZIndexToArrayOrder(overlays: AnyOverlay[]): AnyOverlay[] {
  return overlays.map((overlay, index) => ({
    ...overlay,
    zIndex: index + 1,
  }));
}

/**
 * Creates a default text overlay with predefined settings
 */
export function createDefaultTextOverlay(existingOverlays: AnyOverlay[] = []): TextOverlay {
  return {
    id: generateUniqueId(),
    type: "text",
    text: "Add your text here",
    style: {
      fontFamily: "Arial",
      fontSize: 24,
      color: "#FFFFFF",
      backgroundColor: "#000000",
      opacity: 80,
      textAlign: "center",
      fontWeight: "normal",
      fontStyle: "normal",
      textDecoration: "none",
      lineHeight: 1.4,
      strokeWidthRatio: 0, // Default: No stroke
      strokeColor: "#000000", // Default stroke color (black)
    },
    position: {
      x: 25, // Center a compact text box with room for its editing controls.
      y: 50, // Default middle position, will be overridden based on media type
    },
    rotation: 0, // No rotation by default
    width: 50, // Default width as percentage of container
    zIndex: getNextZIndex(existingOverlays),
    timing: {
      startTime: 0, // Start at the beginning of the video
      endTime: Number.MAX_SAFE_INTEGER, // End at the end of the video (we'll update this with actual duration)
    },
  };
}

/**
 * Available font families for text overlays
 */
export const AVAILABLE_FONTS = [
  "Montserrat",
  "Abel",
  "Yesteryear",
  "Source Code Pro",
  "Roboto Mono",
  "Roboto",
  "Arial",
  "Helvetica",
  "Verdana",
  "Georgia",
  "Times New Roman",
  "Courier New",
  "Comic Sans MS",
  "Impact",
  "Caveat",
  "Permanent Marker",
  "Anton",
  "Pacifico",
  "Bangers",
  "Bebas Neue",
  "Be Vietnam Pro",
  "Lobster",
  "Lato",
  "Oswald",
  "Open Sans",
  "Open Sauce One",
  "Outfit",
  "Raleway",
  "Abril Fatface",
  "Arimo",
  "DM Sans",
  "Inter",
  "Lora",
  "Manrope",
  "Merriweather",
  "Noto Sans Display",
  "Noto Serif Display",
  "Playfair Display",
  "Poppins",
  "PT Serif Caption",
  "Space Grotesk",
  "Bodoni Moda",
  "Red Hat Display",
  "Broking",
  "Gupter",
  "Pinyon Script",
  "Public Sans",
  "DM Serif Display",
  "Architects Daughter",
  "Nunito",
  "Playpen Sans",
  "League Gothic",
  "League Spartan",
  "Libre Baskerville",
  "Barlow Condensed",
  "Erica One",
  "Sanchez",
  "Arapey",
  "Oranienbaum",
  "Noto Serif",
  "Cinzel",
  "Fraunces",
  "Fredoka",
  "Cormorant Garamond",
  "Bricolage Grotesque",
];

/**
 * Fonts restricted to specific user IDs.
 * Key: font name, Value: array of allowed user IDs
 */
export const RESTRICTED_FONTS: Record<string, string[]> = {
  Broking: ["65H3UCcROJSrdIJlfEducP226Nb2", "IgMZtsWFiIcJt4iXuK225xZTjvk1"], // Add allowed user IDs here
};

/**
 * Get available fonts for a specific user, filtering out restricted fonts they don't have access to.
 * @param userId The current user's ID (optional)
 * @returns Array of font names available to this user
 */
export function getAvailableFontsForUser(userId?: string): string[] {
  return AVAILABLE_FONTS.filter((font) => {
    const allowedUsers = RESTRICTED_FONTS[font];
    // If font is not restricted, allow it
    if (!allowedUsers) return true;
    // If font is restricted, check if user is in the allowed list
    return userId ? allowedUsers.includes(userId) : false;
  });
}

/**
 * Available font sizes for text overlays
 */
export const FONT_SIZES = [
  12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72, 80, 88, 96, 104, 112,
  120,
];

// A cache to keep track of fonts that have already been loaded or are in the process of being loaded.
// This prevents redundant `document.fonts.load()` calls for the same font.
const fontLoadCache: { [fontFamily: string]: Promise<void> } = {};

/**
 * Asynchronously loads all unique fonts required by the provided text overlays.
 * It uses a cache to avoid re-loading fonts that are already available.
 * @param overlays An array of overlays to extract font families from.
 */
export async function loadFontsForOverlays(
  overlays: AnyOverlay[]
): Promise<void> {
  const textOverlays = overlays.filter(isTextOverlay);
  if (textOverlays.length === 0) {
    return;
  }

  const uniqueFonts = new Set<string>(
    textOverlays.map((o) => o.style.fontFamily).filter(Boolean) as string[]
  );

  if (uniqueFonts.size === 0) {
    return;
  }

  const fontLoadPromises: Promise<any>[] = [];

  for (const fontFamily of uniqueFonts) {
    // The font-face-source-CSS-syntax requires quoting fonts with spaces.
    // While most browsers are lenient, it's safer to quote.
    const fontSpec = `1em "${fontFamily}"`;

    if (!fontLoadCache[fontFamily]) {
      // Use document.fonts.check() to see if the font is already available.
      // This is more reliable for system fonts which `load()` may fail to find.
      if (document.fonts.check(fontSpec)) {
        fontLoadCache[fontFamily] = Promise.resolve();
      } else {
        // If not locally available, attempt to load it as a web font.
        fontLoadCache[fontFamily] = document.fonts
          .load(fontSpec)
          .then((loadedFonts) => {
            if (loadedFonts.length === 0) {
              console.warn(
                `[Font Loader] Font not found or failed to load: ${fontFamily}. Fallback may be used.`
              );
              // Remove from cache if load "succeeded" but found no font, to allow retries if needed.
              // This can happen if the font name is misspelled or the file is missing on the server.
              delete fontLoadCache[fontFamily];
            }
          })
          .catch((err) => {
            console.error(
              `[Font Loader] Error loading font ${fontFamily}:`,
              err
            );
            delete fontLoadCache[fontFamily]; // Remove from cache on failure to allow retry.
            throw err; // Re-throw to fail Promise.all
          });
      }
    }
    fontLoadPromises.push(fontLoadCache[fontFamily]);
  }

  try {
    await Promise.all(fontLoadPromises);
  } catch (error) {
    console.error(
      "[Font Loader] Failed to load one or more required fonts. Rendering may be incorrect.",
      error
    );
    // We proceed anyway, the browser will use fallback fonts.
  }
}
